import { useMemo } from "react";
import { useProcurement } from "@/context/ProcurementContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/utils";
import { 
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend
} from "recharts";
import { 
  TrendingUp, 
  TrendingDown,
  Award,
  Clock,
  CheckCircle,
  AlertTriangle,
  Star,
  Building2,
  DollarSign,
  Package,
  Users,
  BarChart3
} from "lucide-react";

interface VendorAnalyticsProps {
  onNavigate?: (path: string) => void;
}

export const VendorAnalytics = ({ onNavigate }: VendorAnalyticsProps) => {
  const { state } = useProcurement();

  // Calculate vendor performance metrics
  const vendorMetrics = useMemo(() => {
    return state.vendors.map(vendor => {
      const vendorPOs = state.purchaseOrders.filter(po => po.vendorId === vendor.id);
      const totalOrderValue = vendorPOs.reduce((sum, po) => sum + po.totalAmount, 0);
      const onTimePercentage = vendor.deliveryMetrics.totalOrders > 0 
        ? Math.round((vendor.deliveryMetrics.onTimeDeliveries / vendor.deliveryMetrics.totalOrders) * 100)
        : 0;
      
      return {
        id: vendor.id,
        name: vendor.name,
        code: vendor.vendorCode,
        rating: vendor.performanceRating,
        quality: vendor.deliveryMetrics.qualityScore,
        onTimeDelivery: onTimePercentage,
        responseTime: vendor.deliveryMetrics.responseTime,
        totalOrders: vendor.deliveryMetrics.totalOrders,
        totalValue: totalOrderValue,
        linkedProducts: vendor.linkedSKUs.length,
        isActive: vendor.isActive,
        efficiency: (vendor.performanceRating * onTimePercentage * vendor.deliveryMetrics.qualityScore) / 100
      };
    });
  }, [state.vendors, state.purchaseOrders]);

  // Top performing vendors by different metrics
  const topVendorsByRating = vendorMetrics
    .filter(v => v.isActive)
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 8);

  const topVendorsByValue = vendorMetrics
    .filter(v => v.isActive)
    .sort((a, b) => b.totalValue - a.totalValue)
    .slice(0, 8);

  const topVendorsByOrders = vendorMetrics
    .filter(v => v.isActive)
    .sort((a, b) => b.totalOrders - a.totalOrders)
    .slice(0, 8);

  const topVendorsByEfficiency = vendorMetrics
    .filter(v => v.isActive)
    .sort((a, b) => b.efficiency - a.efficiency)
    .slice(0, 5);

  // Performance distribution
  const performanceDistribution = [
    { 
      name: 'Excellent (4.5+)', 
      value: vendorMetrics.filter(v => v.rating >= 4.5).length, 
      color: '#22c55e',
      percentage: Math.round((vendorMetrics.filter(v => v.rating >= 4.5).length / vendorMetrics.length) * 100)
    },
    { 
      name: 'Good (4.0-4.4)', 
      value: vendorMetrics.filter(v => v.rating >= 4.0 && v.rating < 4.5).length, 
      color: '#3b82f6',
      percentage: Math.round((vendorMetrics.filter(v => v.rating >= 4.0 && v.rating < 4.5).length / vendorMetrics.length) * 100)
    },
    { 
      name: 'Average (3.5-3.9)', 
      value: vendorMetrics.filter(v => v.rating >= 3.5 && v.rating < 4.0).length, 
      color: '#eab308',
      percentage: Math.round((vendorMetrics.filter(v => v.rating >= 3.5 && v.rating < 4.0).length / vendorMetrics.length) * 100)
    },
    { 
      name: 'Poor (<3.5)', 
      value: vendorMetrics.filter(v => v.rating < 3.5).length, 
      color: '#ef4444',
      percentage: Math.round((vendorMetrics.filter(v => v.rating < 3.5).length / vendorMetrics.length) * 100)
    }
  ].filter(item => item.value > 0);

  // Vendor sales frequency (orders per month simulation)
  const salesFrequency = vendorMetrics
    .filter(v => v.totalOrders > 0)
    .map(vendor => ({
      name: vendor.code,
      fullName: vendor.name,
      ordersPerMonth: Math.round(vendor.totalOrders / 6), // Simulate 6 months
      totalOrders: vendor.totalOrders,
      avgOrderValue: vendor.totalValue / vendor.totalOrders || 0
    }))
    .sort((a, b) => b.ordersPerMonth - a.ordersPerMonth)
    .slice(0, 10);

  // Efficiency vs Volume scatter data
  const efficiencyVolumeData = vendorMetrics
    .filter(v => v.isActive && v.totalOrders > 0)
    .map(vendor => ({
      name: vendor.code,
      efficiency: vendor.efficiency,
      volume: vendor.totalValue,
      orders: vendor.totalOrders,
      rating: vendor.rating
    }));

  // Monthly trend simulation (mock data based on actual vendors)
  const monthlyTrend = [
    { month: 'Jul', orders: 45, efficiency: 78, value: 125000 },
    { month: 'Aug', orders: 52, efficiency: 81, value: 142000 },
    { month: 'Sep', orders: 48, efficiency: 79, value: 138000 },
    { month: 'Oct', orders: 61, efficiency: 84, value: 165000 },
    { month: 'Nov', orders: 58, efficiency: 86, value: 159000 },
    { month: 'Dec', orders: 67, efficiency: 88, value: 178000 }
  ];

  // Radar chart data for top 3 vendors
  const radarData = topVendorsByEfficiency.slice(0, 3).map(vendor => ({
    vendor: vendor.code,
    Performance: vendor.rating,
    Quality: vendor.quality,
    'On-Time': vendor.onTimeDelivery / 20, // Scale to 5
    Efficiency: vendor.efficiency / 20, // Scale to 5
    Volume: Math.min(vendor.totalValue / 50000, 5) // Scale to 5
  }));

  const COLORS = ['#22c55e', '#3b82f6', '#eab308', '#ef4444', '#8b5cf6', '#f59e0b'];

  const averageMetrics = {
    rating: vendorMetrics.reduce((sum, v) => sum + v.rating, 0) / vendorMetrics.length || 0,
    onTime: vendorMetrics.reduce((sum, v) => sum + v.onTimeDelivery, 0) / vendorMetrics.length || 0,
    efficiency: vendorMetrics.reduce((sum, v) => sum + v.efficiency, 0) / vendorMetrics.length || 0,
    responseTime: vendorMetrics.reduce((sum, v) => sum + v.responseTime, 0) / vendorMetrics.length || 0
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Vendor Performance Analytics</h2>
          <p className="text-muted-foreground">Comprehensive insights into vendor efficiency and sales patterns</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => onNavigate?.('/vendors')}
          className="flex items-center gap-2"
        >
          <Users className="h-4 w-4" />
          View All Vendors
        </Button>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Award className="h-8 w-8 text-yellow-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Avg Performance</p>
                <p className="text-2xl font-bold">{averageMetrics.rating.toFixed(1)}</p>
                <div className="flex items-center mt-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`h-3 w-3 ${
                        i < Math.floor(averageMetrics.rating) 
                          ? 'text-yellow-400 fill-current' 
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Avg On-Time</p>
                <p className="text-2xl font-bold">{averageMetrics.onTime.toFixed(0)}%</p>
                <Progress value={averageMetrics.onTime} className="h-2 mt-2 w-16" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Efficiency Score</p>
                <p className="text-2xl font-bold">{averageMetrics.efficiency.toFixed(0)}</p>
                <p className="text-xs text-muted-foreground">Composite metric</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Avg Response</p>
                <p className="text-2xl font-bold">{averageMetrics.responseTime.toFixed(0)}h</p>
                <p className="text-xs text-muted-foreground">Response time</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vendor Performance Rating Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Top Vendors by Performance Rating
            </CardTitle>
            <CardDescription>Highest rated vendors based on overall performance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topVendorsByRating}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="code" 
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 5]} />
                  <Tooltip 
                    formatter={(value, name) => [`${value} stars`, 'Rating']}
                    labelFormatter={(label) => {
                      const vendor = topVendorsByRating.find(v => v.code === label);
                      return vendor?.name || label;
                    }}
                  />
                  <Bar dataKey="rating" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Performance Distribution Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Performance Distribution
            </CardTitle>
            <CardDescription>Vendor distribution by performance categories</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={performanceDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {performanceDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} vendors`, name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Frequency Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Vendor Sales Frequency
            </CardTitle>
            <CardDescription>Orders per month by vendor (most active)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={salesFrequency} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    width={80}
                  />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'ordersPerMonth' ? `${value} orders/month` : value,
                      name === 'ordersPerMonth' ? 'Frequency' : name
                    ]}
                    labelFormatter={(label) => {
                      const vendor = salesFrequency.find(v => v.name === label);
                      return vendor?.fullName || label;
                    }}
                  />
                  <Bar dataKey="ordersPerMonth" fill="#22c55e" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Order Value by Vendor */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Top Vendors by Order Value
            </CardTitle>
            <CardDescription>Highest value vendors by total order amount</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topVendorsByValue}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="code" 
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Total Value']}
                    labelFormatter={(label) => {
                      const vendor = topVendorsByValue.find(v => v.code === label);
                      return vendor?.name || label;
                    }}
                  />
                  <Bar dataKey="totalValue" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Monthly Performance Trends
            </CardTitle>
            <CardDescription>Order volume and efficiency trends over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="orders" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    name="Orders"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="efficiency" 
                    stroke="#22c55e" 
                    strokeWidth={2}
                    name="Efficiency %"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Performers Radar */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5" />
              Top 3 Vendor Comparison
            </CardTitle>
            <CardDescription>Multi-dimensional performance comparison</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="vendor" />
                  <PolarRadiusAxis angle={90} domain={[0, 5]} />
                  {radarData.map((_, index) => (
                    <Radar
                      key={index}
                      name={radarData[index]?.vendor}
                      dataKey={Object.keys(radarData[index] || {}).find(key => key !== 'vendor')}
                      stroke={COLORS[index]}
                      fill={COLORS[index]}
                      fillOpacity={0.1}
                    />
                  ))}
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Summary Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Vendor Performance Summary
          </CardTitle>
          <CardDescription>Quick overview of all vendor metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {topVendorsByEfficiency.map((vendor, index) => (
              <div key={vendor.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="font-semibold">{vendor.name}</h4>
                    <p className="text-sm text-muted-foreground">{vendor.code}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`h-3 w-3 ${
                            i < Math.floor(vendor.rating) 
                              ? 'text-yellow-400 fill-current' 
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-sm font-semibold">{vendor.rating.toFixed(1)}</p>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">On-Time</p>
                    <p className="font-semibold">{vendor.onTimeDelivery}%</p>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Orders</p>
                    <p className="font-semibold">{vendor.totalOrders}</p>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Value</p>
                    <p className="font-semibold">{formatCurrency(vendor.totalValue)}</p>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Efficiency</p>
                    <Badge variant={vendor.efficiency > 80 ? "default" : vendor.efficiency > 60 ? "secondary" : "outline"}>
                      {vendor.efficiency.toFixed(0)}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
